import React, { useState, useEffect, useCallback } from 'react';
import {
  Button, Typography, InputNumber, Input, Switch,
  Row, Col, message, Spin, Tooltip, Grid
} from 'antd';
import {
  SaveOutlined, ReloadOutlined, CrownOutlined, RocketOutlined,
  ThunderboltOutlined, PlusOutlined, MinusCircleOutlined,
  CheckCircleOutlined, StopOutlined, InfoCircleOutlined
} from '@ant-design/icons';
import axiosInstance from '../../../../../config/axiosConfig';
import './AdminDashboard.css';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const PLAN_ICONS = {
  starter:      { icon: <ThunderboltOutlined />, color: '#4f6ef7', gradient: 'linear-gradient(135deg, #eef2ff 0%, #dbeafe 100%)' },
  professional: { icon: <RocketOutlined />,      color: '#8b5cf6', gradient: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)' },
  enterprise:   { icon: <CrownOutlined />,       color: '#f59e0b', gradient: 'linear-gradient(135deg, #fefce8 0%, #fef3c7 100%)' },
};

const SubscriptionPlansPage = () => {
  const [plans, setPlans] = useState([]);
  const [editedPlans, setEditedPlans] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newFeatureInputs, setNewFeatureInputs] = useState({});

  const screens = useBreakpoint();
  const isMobile = !screens.sm;

  // ── Fetch plans ──
  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get('/admin/platform/plans');
      if (res.data?.status !== false && res.data?.entity?.plans) {
        setPlans(res.data.entity.plans);
        setEditedPlans({});
        setNewFeatureInputs({});
      } else {
        message.error(res.data?.message || 'Failed to fetch plans');
      }
    } catch (err) {
      console.error('Error fetching plans:', err);
      message.error('Failed to fetch subscription plans');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  // ── Edit helpers ──
  const getEdited = (planid) => editedPlans[planid] || {};

  const getVal = (plan, field) => {
    const e = getEdited(plan.planid);
    return e[field] !== undefined ? e[field] : plan[field];
  };

  const setField = (planid, field, value) => {
    setEditedPlans(prev => ({
      ...prev,
      [planid]: { ...(prev[planid] || {}), [field]: value }
    }));
  };

  const getFeatures = (plan) => {
    const e = getEdited(plan.planid);
    return e.features !== undefined ? e.features : (plan.features || []);
  };

  const removeFeature = (planid, plan, index) => {
    const current = [...getFeatures(plan)];
    current.splice(index, 1);
    setField(planid, 'features', current);
  };

  const addFeature = (planid, plan) => {
    const text = (newFeatureInputs[planid] || '').trim();
    if (!text) return;
    const current = [...getFeatures(plan)];
    current.push(text);
    setField(planid, 'features', current);
    setNewFeatureInputs(prev => ({ ...prev, [planid]: '' }));
  };

  const isPlanEdited = (planid) => Object.keys(getEdited(planid)).length > 0;
  const hasAnyChanges = Object.keys(editedPlans).some(id => Object.keys(editedPlans[id]).length > 0);

  // ── Save ──
  const handleSaveAll = async () => {
    if (!hasAnyChanges) { message.info('No changes to save'); return; }

    const payload = plans
      .filter(p => isPlanEdited(p.planid))
      .map(p => {
        const e = getEdited(p.planid);
        return {
          planid: p.planid,
          monthly_price: e.monthly_price !== undefined ? Number(e.monthly_price) : Number(p.monthly_price),
          annual_price:  e.annual_price  !== undefined ? Number(e.annual_price)  : Number(p.annual_price),
          credits:       e.credits       !== undefined ? Number(e.credits)       : Number(p.credits),
          email_limit:   e.email_limit   !== undefined ? Number(e.email_limit)   : Number(p.email_limit || 0),
          whatsapp_campaign_limit: e.whatsapp_campaign_limit !== undefined ? Number(e.whatsapp_campaign_limit) : Number(p.whatsapp_campaign_limit || 0),
          is_unlimited_whatsapp: e.is_unlimited_whatsapp !== undefined ? e.is_unlimited_whatsapp : (p.is_unlimited_whatsapp || false),
          features:      e.features      !== undefined ? e.features              : p.features,
          description:   e.description   !== undefined ? e.description           : p.description,
          display_name:  e.display_name  !== undefined ? e.display_name          : p.display_name,
          is_active:     e.is_active     !== undefined ? e.is_active             : p.is_active,
          monthly_validity_days: e.monthly_validity_days !== undefined ? Number(e.monthly_validity_days) : Number(p.monthly_validity_days || 30),
          annual_validity_days:  e.annual_validity_days  !== undefined ? Number(e.annual_validity_days)  : Number(p.annual_validity_days || 365),
        };
      });

    try {
      setSaving(true);
      const res = await axiosInstance.put('/admin/platform/plans', { plans: payload });
      if (res.data?.status !== false) {
        message.success('Plans updated successfully');
        fetchPlans();
      } else {
        message.error(res.data?.message || 'Update failed');
      }
    } catch (err) {
      console.error('Error updating plans:', err);
      message.error('Failed to update plans');
    } finally {
      setSaving(false);
    }
  };

  if (loading && plans.length === 0) {
    return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;
  }

  return (
    <div>
      {/* Header bar */}
      <div className="admin-page-header" style={{ marginBottom: 20 }}>
        <div className="admin-page-header-left">
          <div className="admin-page-header-icon" style={{ background: '#fef3c7', color: '#f59e0b' }}>
            <CrownOutlined />
          </div>
          <div>
            <h2 className="admin-page-header-title" style={{ fontSize: 17 }}>Subscription Plans</h2>
            <p className="admin-page-header-subtitle">Manage pricing, features & quotas</p>
          </div>
        </div>
        <div className="admin-page-header-actions">
          <Button icon={<ReloadOutlined />} onClick={fetchPlans} loading={loading} className="admin-btn-secondary">Refresh</Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSaveAll}
            loading={saving}
            disabled={!hasAnyChanges}
            className="admin-btn-primary"
          >
            Save All Changes
          </Button>
        </div>
      </div>

      {/* Plan cards */}
      <Row gutter={[20, 20]}>
        {plans.map((plan) => {
          const meta = PLAN_ICONS[plan.plan_name] || PLAN_ICONS.starter;
          const edited = isPlanEdited(plan.planid);
          const features = getFeatures(plan);
          const isActive = getVal(plan, 'is_active');

          return (
            <Col xs={24} sm={12} lg={8} key={plan.planid}>
              <div
                className={`admin-plan-card ${!isActive ? 'inactive' : ''} ${edited ? 'modified' : ''}`}
              >
                  {/* Plan header */}
                  <div className="admin-plan-header" style={{ background: meta.gradient }}>
                    <div className="admin-plan-icon" style={{ background: `${meta.color}18`, color: meta.color }}>
                      {meta.icon}
                    </div>
                    <Input
                      className="admin-plan-name-input"
                      value={getVal(plan, 'display_name')}
                      onChange={(e) => setField(plan.planid, 'display_name', e.target.value)}
                      style={{
                        textAlign: 'center', fontWeight: 700, fontSize: isMobile ? 14 : 17,
                        border: 'none', background: 'transparent', width: '80%',
                        color: '#1a1d26', letterSpacing: '-0.01em'
                      }}
                    />
                    <div className="admin-plan-status">
                      <span style={{ fontSize: 12, color: '#6b7280' }}>{isActive ? 'Active' : 'Inactive'}</span>
                      <Switch
                        checked={isActive}
                        onChange={(checked) => setField(plan.planid, 'is_active', checked)}
                        checkedChildren={<CheckCircleOutlined />}
                        unCheckedChildren={<StopOutlined />}
                        size="small"
                      />
                    </div>
                  </div>

                  <div className="admin-plan-body">
                  <div className="admin-plan-block">
                    {/* Pricing fields */}
                    <div className="admin-plan-section-title">Pricing</div>
                    <Row className="admin-plan-grid" gutter={[isMobile ? 8 : 12, isMobile ? 8 : 12]}>
                      <Col span={12}>
                        <Text type="secondary" style={{ fontSize: 12 }}>Monthly Price</Text>
                        <InputNumber
                          value={getVal(plan, 'monthly_price')}
                          onChange={(v) => setField(plan.planid, 'monthly_price', v)}
                          min={0} step={1} precision={2} prefix="₹"
                          style={{ width: '100%', fontSize: isMobile ? 12 : 14 }}
                          controls={false}
                        />
                      </Col>
                      <Col span={12}>
                        <Text type="secondary" style={{ fontSize: 12 }}>Annual Price</Text>
                        <InputNumber
                          value={getVal(plan, 'annual_price')}
                          onChange={(v) => setField(plan.planid, 'annual_price', v)}
                          min={0} step={1} precision={2} prefix="₹"
                          style={{ width: '100%', fontSize: isMobile ? 12 : 14 }}
                          controls={false}
                        />
                      </Col>
                    </Row>

                    {/* Validity fields */}
                    <Row className="admin-plan-grid" gutter={[isMobile ? 8 : 12, isMobile ? 8 : 12]} style={{ marginTop: isMobile ? 8 : 12 }}>
                      <Col span={12}>
                        <Tooltip title="Number of days the monthly plan is valid after purchase">
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Monthly Validity (days) <InfoCircleOutlined style={{ color: '#999', fontSize: 11 }} />
                          </Text>
                        </Tooltip>
                        <InputNumber
                          value={getVal(plan, 'monthly_validity_days') || 30}
                          onChange={(v) => setField(plan.planid, 'monthly_validity_days', v)}
                          min={1} step={1}
                          style={{ width: '100%' }}
                          controls={false}
                          suffix="days"
                        />
                      </Col>
                      <Col span={12}>
                        <Tooltip title="Number of days the annual plan is valid after purchase">
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Annual Validity (days) <InfoCircleOutlined style={{ color: '#999', fontSize: 11 }} />
                          </Text>
                        </Tooltip>
                        <InputNumber
                          value={getVal(plan, 'annual_validity_days') || 365}
                          onChange={(v) => setField(plan.planid, 'annual_validity_days', v)}
                          min={1} step={1}
                          style={{ width: '100%' }}
                          controls={false}
                          suffix="days"
                        />
                      </Col>
                    </Row>
                  </div>

                  <div className="admin-plan-block">
                    {/* Plan Quota Fields */}
                    <div className="admin-plan-section-title" style={{ marginTop: 0 }}>Quotas</div>

                    <Row className="admin-plan-grid" gutter={[12, 12]} style={{ marginTop: 8 }}>
                      <Col span={12}>
                        <Tooltip title="Number of emails included in this plan per billing cycle">
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Email Limit <InfoCircleOutlined style={{ color: '#999', fontSize: 11 }} />
                          </Text>
                        </Tooltip>
                        <InputNumber
                          value={getVal(plan, 'email_limit') || 0}
                          onChange={(v) => setField(plan.planid, 'email_limit', v)}
                          min={0} step={1000}
                          style={{ width: '100%' }}
                          controls={false}
                          formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                          parser={(v) => v.replace(/,/g, '')}
                        />
                      </Col>
                      <Col span={12}>
                        <Tooltip title="Number of WhatsApp campaigns allowed per billing cycle (0 = use unlimited toggle)">
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            WA Campaign Limit <InfoCircleOutlined style={{ color: '#999', fontSize: 11 }} />
                          </Text>
                        </Tooltip>
                        <InputNumber
                          value={getVal(plan, 'whatsapp_campaign_limit') || 0}
                          onChange={(v) => setField(plan.planid, 'whatsapp_campaign_limit', v)}
                          min={0} step={10}
                          style={{ width: '100%' }}
                          controls={false}
                          disabled={getVal(plan, 'is_unlimited_whatsapp')}
                        />
                      </Col>
                    </Row>

                    <div className="admin-plan-inline" style={{ marginTop: 8 }}>
                      <Tooltip title="If enabled, this plan has unlimited WhatsApp campaigns">
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Unlimited WA Campaigns <InfoCircleOutlined style={{ color: '#999', fontSize: 11 }} />
                        </Text>
                      </Tooltip>
                      <Switch
                        checked={getVal(plan, 'is_unlimited_whatsapp') || false}
                        onChange={(checked) => setField(plan.planid, 'is_unlimited_whatsapp', checked)}
                        checkedChildren="Yes"
                        unCheckedChildren="No"
                        size="small"
                      />
                    </div>

                    <div className="admin-plan-desc" style={{ marginTop: 12 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>Description</Text>
                      <Input.TextArea
                        value={getVal(plan, 'description') || ''}
                        onChange={(e) => setField(plan.planid, 'description', e.target.value)}
                        rows={2}
                        placeholder="Plan description"
                        style={{ resize: 'none' }}
                      />
                    </div>
                  </div>

                  <div className="admin-plan-block admin-plan-block--features">
                    {/* Features */}
                    <div className="admin-plan-section-title" style={{ marginTop: 0 }}>Features</div>
                    <div className="admin-plan-features">
                      {features.map((f, idx) => (
                        <div
                          key={idx}
                          className="admin-plan-feature"
                        >
                          <Text style={{ fontSize: 13 }}>
                            <CheckCircleOutlined style={{ color: meta.color, marginRight: 6, fontSize: 12 }} />
                            {f}
                          </Text>
                          <Tooltip title="Remove feature">
                            <MinusCircleOutlined
                              style={{ color: '#ff4d4f', cursor: 'pointer', fontSize: 14 }}
                              onClick={() => removeFeature(plan.planid, plan, idx)}
                            />
                          </Tooltip>
                        </div>
                      ))}
                    </div>

                    {/* Add feature */}
                    <div className="admin-plan-add-feature">
                      <Input
                        size="small"
                        placeholder="Add feature..."
                        value={newFeatureInputs[plan.planid] || ''}
                        onChange={(e) => setNewFeatureInputs(prev => ({ ...prev, [plan.planid]: e.target.value }))}
                        onPressEnter={() => addFeature(plan.planid, plan)}
                      />
                      <Button
                        size="small"
                        type="dashed"
                        icon={<PlusOutlined />}
                        onClick={() => addFeature(plan.planid, plan)}
                      />
                    </div>
                  </div>

                  {/* Edited indicator */}
                  {edited && (
                    <div className="admin-plan-footer">
                      <InfoCircleOutlined style={{ color: '#f59e0b' }} />
                      Unsaved changes — save to apply
                    </div>
                  )}
                  </div>
              </div>
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

export default SubscriptionPlansPage;
